/**
 * Локали экосистемы. Хранятся в БД как TEXT ISO 639-1 (никогда enum).
 *
 * `ECOSYSTEM_LOCALES` — целевой набор ЦМО. Он обязан совпадать с
 * `SUPPORTED_LOCALES` StudyHub (`apps/web/src/i18n/locales.ts`) и `LOCALES`
 * IEC Hero (`packages/shared/src/constants/roles.ts`): профиль один на всю
 * экосистему, и локаль, которую примет Hero, но отвергнет IEC ID, означала бы
 * молчаливую подмену языка при переносе профиля.
 *
 * `ACTIVE_LOCALES` — то, для чего у нас есть словари. С 21.08.2026 (декрет Д11)
 * это ВЕСЬ набор: словари недостающих локалей заводит `scripts/i18n-sync-locales.mjs`
 * копией английского, поэтому непереведённый ключ показывается по-английски, а
 * не по-русски. Раньше активными были `ru`+`en`, и любой нерусский пользователь
 * с непереведённым ключом получал русский текст — ровно то, что Д11 запрещает.
 *
 * Порядок — `en` первым, дальше ISO A→Z: первым в списке стоит основной язык
 * сервиса, а не родной язык команды.
 */
export const ECOSYSTEM_LOCALES = [
	"en",
	"az",
	"be",
	"de",
	"el",
	"es",
	"fr",
	"hi",
	"hu",
	"hy",
	"it",
	"ka",
	"kk",
	"lv",
	"pl",
	"pt",
	"ru",
	"tr",
	"uk",
	"uz",
	"zh",
] as const;

export type EcosystemLocale = (typeof ECOSYSTEM_LOCALES)[number];

export function isEcosystemLocale(value: string): value is EcosystemLocale {
	return (ECOSYSTEM_LOCALES as readonly string[]).includes(value);
}

/**
 * Активные локали = весь набор экосистемы (Д11).
 *
 * Не копия списка, а ссылка: два списка разъезжаются, а «локаль есть в профиле,
 * но её нет в i18next.supportedLngs» означает молчаливый откат на фолбэк без
 * единой ошибки — самый дорогой вид расхождения, он виден только пользователю.
 */
export const ACTIVE_LOCALES = ECOSYSTEM_LOCALES;

/**
 * Язык по умолчанию и язык фолбэка — английский, одно значение (Д11).
 *
 * Раньше `DEFAULT_LOCALE` был `"ru"`, а `FALLBACK_LOCALE` — `"en"`, и это
 * расхождение и было дырой: всё, что не знало локали пользователя (импорт,
 * письмо по событию, форматирование на сервере), молча выбирало русский.
 */
export const DEFAULT_LOCALE = "en";
export const FALLBACK_LOCALE = "en";

export type ActiveLocale = (typeof ACTIVE_LOCALES)[number];

export function isActiveLocale(value: string): value is ActiveLocale {
	return (ACTIVE_LOCALES as readonly string[]).includes(value);
}

/**
 * Категории множественного числа CLDR — справочно, для авторов словарей.
 *
 * Ключ `{count, plural, …}` без нужной категории не падает: ICU молча берёт
 * `other`, и «1 Балл» превращается в «1 Баллов». Список здесь — чтобы автор
 * перевода видел, какие ветки обязан заполнить, не открывая CLDR.
 *
 * Значения не выводятся из `Intl.PluralRules`: набор категорий зависит от
 * версии ICU в движке (в es/it/pt категория `many` появилась только в CLDR 42),
 * а словари собираются один раз и должны быть одинаковыми на всех рантаймах.
 */
export type PluralCategory = "zero" | "one" | "two" | "few" | "many" | "other";

/** Направление письма. Все 21 локаль — ltr; ветка rtl появится с `ar`/`he`. */
export type TextDirection = "ltr" | "rtl";

/**
 * Подписи локалей — те же строки, что в StudyHub (`i18n/locales.ts::LOCALE_META`)
 * и Hero (`i18n/locales-registry.ts::LOCALE_META`).
 *
 * Совпадение здесь не косметика: человек переходит между сервисами экосистемы с
 * одним профилем, и «Қазақ тілі» в Hero против «Казахский» у нас читается как
 * разные настройки. Поэтому строки скопированы, а не выведены из
 * `Intl.DisplayNames`: тот отдаёт форму, зависящую от движка и версии ICU
 * («русский» со строчной, «Kazakh» вместо эндонима), и три сервиса разъехались
 * бы сами собой.
 *
 * `shortLabel` — компактный ярлык для триггера в шапке. Он намеренно расходится
 * с ISO там, где привычнее код страны (`zh→CN`, `hy→AM`, `hi→IN`, `ka→GE`,
 * `be→BY`, `uk→UA`) — ровно как в Hero.
 *
 * `englishName` наружу не показывается — он нужен поиску. Эндоним находит только
 * тот, кто уже умеет писать на этом языке: «gr» не даёт «Ελληνικά», и человек
 * решает, что греческого в списке нет.
 *
 * `satisfies Record<EcosystemLocale, …>` — гейт: новый код в
 * `ECOSYSTEM_LOCALES` без подписи здесь роняет билд, а не молча показывает
 * человеку «PT» вместо названия языка.
 */
export const LOCALE_META = {
	en: {
		nativeName: "English",
		shortLabel: "EN",
		englishName: "English",
		dir: "ltr",
		plurals: ["one", "other"],
	},
	az: {
		nativeName: "Azərbaycan",
		shortLabel: "AZ",
		englishName: "Azerbaijani",
		dir: "ltr",
		plurals: ["one", "other"],
	},
	be: {
		nativeName: "Беларуская",
		shortLabel: "BY",
		englishName: "Belarusian",
		dir: "ltr",
		plurals: ["one", "few", "many", "other"],
	},
	de: {
		nativeName: "Deutsch",
		shortLabel: "DE",
		englishName: "German",
		dir: "ltr",
		plurals: ["one", "other"],
	},
	el: {
		nativeName: "Ελληνικά",
		shortLabel: "EL",
		englishName: "Greek",
		dir: "ltr",
		plurals: ["one", "other"],
	},
	es: {
		nativeName: "Español",
		shortLabel: "ES",
		englishName: "Spanish",
		dir: "ltr",
		plurals: ["one", "many", "other"],
	},
	fr: {
		nativeName: "Français",
		shortLabel: "FR",
		englishName: "French",
		dir: "ltr",
		plurals: ["one", "many", "other"],
	},
	hi: {
		nativeName: "हिन्दी",
		shortLabel: "IN",
		englishName: "Hindi",
		dir: "ltr",
		plurals: ["one", "other"],
	},
	hu: {
		nativeName: "Magyar",
		shortLabel: "HU",
		englishName: "Hungarian",
		dir: "ltr",
		plurals: ["one", "other"],
	},
	hy: {
		nativeName: "Հայերեն",
		shortLabel: "AM",
		englishName: "Armenian",
		dir: "ltr",
		plurals: ["one", "other"],
	},
	it: {
		nativeName: "Italiano",
		shortLabel: "IT",
		englishName: "Italian",
		dir: "ltr",
		plurals: ["one", "many", "other"],
	},
	ka: {
		nativeName: "ქართული",
		shortLabel: "GE",
		englishName: "Georgian",
		dir: "ltr",
		plurals: ["one", "other"],
	},
	kk: {
		nativeName: "Қазақ тілі",
		shortLabel: "KZ",
		englishName: "Kazakh",
		dir: "ltr",
		plurals: ["one", "other"],
	},
	lv: {
		nativeName: "Latviešu",
		shortLabel: "LV",
		englishName: "Latvian",
		dir: "ltr",
		plurals: ["zero", "one", "other"],
	},
	pl: {
		nativeName: "Polski",
		shortLabel: "PL",
		englishName: "Polish",
		dir: "ltr",
		plurals: ["one", "few", "many", "other"],
	},
	pt: {
		nativeName: "Português",
		shortLabel: "PT",
		englishName: "Portuguese",
		dir: "ltr",
		plurals: ["one", "many", "other"],
	},
	ru: {
		nativeName: "Русский",
		shortLabel: "RU",
		englishName: "Russian",
		dir: "ltr",
		plurals: ["one", "few", "many", "other"],
	},
	tr: {
		nativeName: "Türkçe",
		shortLabel: "TR",
		englishName: "Turkish",
		dir: "ltr",
		plurals: ["one", "other"],
	},
	uk: {
		nativeName: "Українська",
		shortLabel: "UA",
		englishName: "Ukrainian",
		dir: "ltr",
		plurals: ["one", "few", "many", "other"],
	},
	uz: {
		nativeName: "Oʻzbek",
		shortLabel: "UZ",
		englishName: "Uzbek",
		dir: "ltr",
		plurals: ["one", "other"],
	},
	zh: {
		nativeName: "中文",
		shortLabel: "CN",
		englishName: "Chinese",
		dir: "ltr",
		plurals: ["other"],
	},
} as const satisfies Record<
	EcosystemLocale,
	{
		nativeName: string;
		shortLabel: string;
		englishName: string;
		dir: TextDirection;
		plurals: readonly PluralCategory[];
	}
>;

export type LocaleMeta = {
	readonly nativeName: string;
	readonly shortLabel: string;
	readonly englishName: string;
	readonly dir: TextDirection;
	readonly plurals: readonly PluralCategory[];
};

/**
 * Приведение чего угодно к коду локали экосистемы.
 *
 * Одна функция на весь монорепо: детектор браузера отдаёт `ru-RU`, Google —
 * `zh-CN`, письмо из старого портала — `PT-br`, а профиль хранит две буквы. Без
 * общего приведения каждый вызов чинил это по-своему, и `zh-CN` то находил
 * словарь, то нет.
 *
 * Неизвестный, пустой и мусорный код → `en` (Д11): русский не должен доставаться
 * тому, про кого мы ничего не знаем.
 *
 * Региональный суффикс отбрасывается, а не сопоставляется: отдельных словарей
 * `pt-BR` / `zh-TW` у нас нет, и делать вид, что есть, — значит однажды
 * показать бразильцу португальский из Португалии молча, вместо явного решения.
 */
export function normalizeLocale(raw: string | null | undefined): EcosystemLocale {
	if (!raw) return FALLBACK_LOCALE;
	const base = raw.trim().toLowerCase().split(/[-_]/)[0] ?? "";
	return isEcosystemLocale(base) ? base : FALLBACK_LOCALE;
}

/**
 * Порядок в пикерах: `en` первым, остальные по ISO A→Z.
 *
 * Тот же порядок, что у `sortLocalesForPicker` StudyHub и `getLocalesForPicker`
 * Hero. Русский специально не поднят наверх: его позиция — после `pt`. Список,
 * начинающийся с родного языка команды, читается как «главный язык сервиса», а
 * главный здесь английский.
 */
export function localesForPicker(codes: readonly string[] = ACTIVE_LOCALES): readonly string[] {
	const rest = codes.filter((code) => code !== "en").toSorted((a, b) => a.localeCompare(b, "en"));
	return codes.includes("en") ? ["en", ...rest] : rest;
}
